import { Stack, Typography } from '@mui/material';
import type { PaletteColor } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl, FormattedListParts } from 'react-intl';

import { ScoreDoughnutChart } from '@components/Charts/Doughnut';
import Icon from '@components/Icon/Icon';

// import RadarChart from '@components/Charts/Radar/RadarChart';
import type { Threshold } from '@constants/health-score-info';
import {
  getHealthScoreInfo,
  HealthScoreType,
} from '@constants/health-score-info';

interface HealthScoreInfoModalContentProps {
  metricId: string;
  isEmpty: boolean;
}

const messages = defineMessages({
  howCalculated: {
    id: 'healthScores.info.subtitle',
    defaultMessage: 'How was this calculated?',
  },
  whatMeans: {
    id: 'healthScores.info.whatMeans',
    defaultMessage: 'What does this score mean?',
  },
  howCalculatedSection: {
    id: 'healthScores.info.howCalculated',
    defaultMessage: 'How is it calculated?',
  },
  weightedAverage: {
    id: 'healthScores.weightedAverage',
    defaultMessage: 'Based on a weighted average of: {calculation}.',
  },
  mandatory: {
    id: 'healthScores.mandatory',
    defaultMessage:
      '* Indicates a required metric for health score calculation',
  },
});

const formatThresholdRange = (t: Threshold) => {
  if (t.lower === 0) return `<${t.upper}`;
  if (t.upper === 100) return `${t.lower}+`;

  const upper = t.upperInclusive === false ? t.upper - 1 : t.upper;
  return `${t.lower}-${upper}`;
};

const HealthScoreInfoModalContent: React.FC<
  HealthScoreInfoModalContentProps
> = ({ metricId, isEmpty }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const info = getHealthScoreInfo(metricId);

  /*
  const chartData = {
    labels: info.factors.map((factor) => formatMessage(factor)),
    datasets: [
      {
        label: formatMessage(info.title),
        data: info.data,
        backgroundColor: `${theme.palette.primary.main}33`,
        borderColor: theme.palette.primary.main,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: theme.palette.divider,
        pointHoverRadius: 6,
        pointRadius: 5,
        borderWidth: 2,
        tension: 0,
      },
    ],
  };
  */

  return (
    <Stack gap={0.5}>
      {isEmpty && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ lineHeight: 1.4, fontSize: 14 }}
        >
          {formatMessage(info.disc)}
        </Typography>
      )}

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: theme.palette.primary.main }}
      >
        {formatMessage(messages.whatMeans)}
      </Typography>

      <Typography variant="body2">{formatMessage(info.desc)}</Typography>

      <div>
        {info.thresholds.map((t, i) => (
          <Typography key={i} variant="body2">
            <span
              style={{
                color: (theme.palette[t.color] as PaletteColor).main,
                fontWeight: 600,
              }}
            >
              {formatThresholdRange(t)} {formatMessage(t.label)}:
            </span>{' '}
            {formatMessage(t.message)}
          </Typography>
        ))}
      </div>

      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: theme.palette.primary.main }}
      >
        {formatMessage(messages.howCalculatedSection)}
      </Typography>

      {/* <Typography variant="body2">{formatMessage(info.calc)}</Typography> */}

      <Stack direction="row">
        <Typography variant="body2">
          {formatMessage(messages.weightedAverage, {
            calculation: (
              <FormattedListParts
                type="conjunction"
                value={info.factors ?? info.chartFactors}
              >
                {(parts) => {
                  return (
                    <>
                      {parts.map((part, i) => {
                        const key = `${i}`;
                        const elementIndex = i / 2;

                        // Some sleep score factors are mandatory and optional - display mandatory with an *
                        const showAsterick =
                          part.value === 'healthScores.factors.deepSleep' ||
                          part.value === 'healthScores.factors.remSleep' ||
                          part.value === 'healthScores.factors.lightSleep' ||
                          part.value === 'healthScores.factors.totalSleep';

                        // Determine and display colour
                        const scoreColours = info.colors.at(elementIndex);
                        const colourPalette = (
                          scoreColours?.primary
                            ? theme.palette[scoreColours.primary]
                            : theme.palette['primary']
                        ) as PaletteColor;

                        const colourKey =
                          scoreColours?.secondary &&
                          colourPalette[
                            scoreColours.secondary as keyof PaletteColor
                          ]
                            ? (scoreColours.secondary as keyof PaletteColor)
                            : 'main';
                        const colour = colourPalette[colourKey];

                        // 'part.type' can be 'element' (the item text) or 'literal' ('and', ', ')
                        switch (part.type) {
                          case 'element':
                            return (
                              <span
                                key={key}
                                style={{
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Icon
                                  color={colour}
                                  name="circle"
                                  size="2xs"
                                  style={{ marginRight: '4px' }}
                                  type="fas"
                                />
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'wrap',
                                  }}
                                >
                                  {formatMessage({ id: part.value })}
                                  {showAsterick ? '*' : ''} (
                                  {info.data.at(elementIndex)}%)
                                </Typography>
                              </span>
                            );
                          case 'literal':
                            return <span key={key}>{part.value}</span>;
                        }
                      })}
                    </>
                  );
                }}
              </FormattedListParts>
            ),
          })}
        </Typography>
      </Stack>

      {metricId === HealthScoreType.Sleep && (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          {formatMessage(messages.mandatory)}
        </Typography>
      )}

      {/* <RadarChart data={chartData} height={500} /> */}
      <ScoreDoughnutChart metricId={metricId} />
    </Stack>
  );
};

export default HealthScoreInfoModalContent;
